import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { requestNotificationPermission } from "@/lib/reminders";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Bell, Plus, Trash2, Bot, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { id: "meal", label: "Meal" },
  { id: "glucose", label: "Glucose check" },
  { id: "bp", label: "Blood pressure" },
  { id: "weight", label: "Weight check-in" },
  { id: "medication", label: "Medication" },
  { id: "water", label: "Hydration" },
];

const DAYS = [
  { id: 0, short: "S", label: "Sun" },
  { id: 1, short: "M", label: "Mon" },
  { id: 2, short: "T", label: "Tue" },
  { id: 3, short: "W", label: "Wed" },
  { id: 4, short: "T", label: "Thu" },
  { id: 5, short: "F", label: "Fri" },
  { id: 6, short: "S", label: "Sat" },
];

const TYPE_DEFAULTS = {
  meal: { label: "Eat your planned meal", time: "12:00" },
  glucose: { label: "Check fasting glucose", time: "07:30" },
  bp: { label: "Check blood pressure", time: "08:00" },
  weight: { label: "Weigh-in", time: "07:00" },
  medication: { label: "Take medication", time: "08:00" },
  water: { label: "Drink water", time: "10:00" },
};

export default function Reminders() {
  const [rows, setRows] = useState([]);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/reminders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const enableNotifications = async () => {
    const r = await requestNotificationPermission();
    setPermission(r);
    if (r === "granted") toast.success("Notifications enabled");
    else if (r === "denied") toast.error("Notifications blocked. Enable in browser settings.");
  };

  const remove = async (id) => {
    await api.delete(`/reminders/${id}`);
    load();
  };

  const toggle = async (rem) => {
    await api.put(`/reminders/${rem.id}`, { ...rem, enabled: !rem.enabled });
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-8 fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Reminders</h1>
        <p className="text-slate-500 mt-2">Schedule meal and reading nudges in your browser.</p>
      </div>

      {/* Permission card */}
      <Card className={`rounded-3xl border ${permission === "granted" ? "border-emerald-100 bg-emerald-50/40" : "border-sky-100 bg-sky-50/40"} p-5 mb-6 fade-up delay-1`} data-testid="permission-card">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${permission === "granted" ? "bg-emerald-100" : "bg-sky-100"}`}>
            {permission === "granted" ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            ) : (
              <Bell className="w-5 h-5 text-sky-700" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {permission === "granted" && (
              <>
                <div className="font-medium text-slate-900">Browser notifications enabled</div>
                <div className="text-xs text-slate-500 mt-0.5">Reminders fire while NutriGuide is open in any tab.</div>
              </>
            )}
            {permission !== "granted" && permission !== "denied" && permission !== "unsupported" && (
              <>
                <div className="font-medium text-slate-900">Turn on notifications</div>
                <div className="text-xs text-slate-500 mt-0.5">We need browser permission to send reminders.</div>
              </>
            )}
            {permission === "denied" && (
              <>
                <div className="font-medium text-slate-900">Notifications are blocked</div>
                <div className="text-xs text-slate-500 mt-0.5">Re-enable in your browser&apos;s site settings to receive reminders.</div>
              </>
            )}
            {permission === "unsupported" && (
              <>
                <div className="font-medium text-slate-900">Browser notifications not supported</div>
                <div className="text-xs text-slate-500 mt-0.5">Your browser doesn&apos;t support web notifications.</div>
              </>
            )}
          </div>
          {permission !== "granted" && permission !== "unsupported" && (
            <Button onClick={enableNotifications} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="enable-notif-btn">
              Enable
            </Button>
          )}
        </div>
      </Card>

      {/* List */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">{rows.length} reminder{rows.length === 1 ? "" : "s"}</div>
        <Button onClick={() => setShowForm(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-reminder-btn">
          <Plus className="w-4 h-4 mr-1" /> New reminder
        </Button>
      </div>

      {showForm && (
        <ReminderForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      <div className="space-y-2 mt-4" data-testid="reminder-list">
        {rows.length === 0 && (
          <Card className="rounded-3xl border-dashed border-emerald-200 p-12 text-center">
            <Bot className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No reminders yet. Add your first one above.</p>
          </Card>
        )}
        {rows.map((r) => (
          <Card key={r.id} className={`rounded-2xl border-emerald-100 p-4 bg-white flex items-center gap-4 ${!r.enabled ? "opacity-60" : ""}`} data-testid={`reminder-${r.id}`}>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold text-slate-900">{r.label}</span>
                <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">{TYPES.find((t) => t.id === r.type)?.label}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                <span><b className="text-slate-700">{r.time}</b></span>
                <span>{r.days?.length === 7 ? "Every day" : DAYS.filter((d) => r.days?.includes(d.id)).map((d) => d.label).join(", ")}</span>
                {r.medication_name && <span>• {r.medication_name}</span>}
              </div>
            </div>
            <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-${r.id}`} />
            <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}>
              <Trash2 className="w-4 h-4 text-slate-400" />
            </Button>
          </Card>
        ))}
      </div>

      {permission !== "granted" && rows.length > 0 && (
        <div className="mt-6 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-xl p-3 border border-amber-100">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Reminders are saved but won&apos;t fire until you enable browser notifications above.</span>
        </div>
      )}
    </div>
  );
}

function ReminderForm({ onClose, onSaved }) {
  const [type, setType] = useState("meal");
  const [label, setLabel] = useState(TYPE_DEFAULTS["meal"].label);
  const [time, setTime] = useState(TYPE_DEFAULTS["meal"].time);
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [medName, setMedName] = useState("");
  const [busy, setBusy] = useState(false);

  const onTypeChange = (v) => {
    setType(v);
    const d = TYPE_DEFAULTS[v];
    if (d) { setLabel(d.label); setTime(d.time); }
  };

  const toggleDay = (id) => {
    setDays((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id].sort());
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reminders", {
        type, label, time, days,
        enabled: true,
        medication_name: type === "medication" ? (medName || null) : null,
      });
      toast.success("Reminder created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-3xl border-emerald-100 p-6 fade-up" data-testid="reminder-form">
      <form onSubmit={save} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={onTypeChange}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="reminder-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Time</Label>
            <Input type="time" className="rounded-xl mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reminder-time" />
          </div>
        </div>
        <div>
          <Label>Label</Label>
          <Input className="rounded-xl mt-1.5" value={label} onChange={(e) => setLabel(e.target.value)} required data-testid="reminder-label" />
        </div>
        {type === "medication" && (
          <div>
            <Label>Medication name (optional)</Label>
            <Input className="rounded-xl mt-1.5" value={medName} onChange={(e) => setMedName(e.target.value)} placeholder="e.g. Metformin 500mg" />
          </div>
        )}
        <div>
          <Label>Days</Label>
          <div className="flex gap-1.5 mt-1.5">
            {DAYS.map((d) => {
              const on = days.includes(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDay(d.id)}
                  data-testid={`reminder-day-${d.id}`}
                  className={`w-9 h-9 rounded-full text-xs font-medium border transition-all ${
                    on
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-emerald-100 bg-white text-slate-500"
                  }`}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-reminder">
            {busy ? "Saving…" : "Save reminder"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
